import { computed, inject, Injectable, InjectionToken, signal } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { AuthService } from '@core/authentication/auth.service';
import { isInspectorUser } from '@core/authentication/auth.utility';
import { TenantService } from '@core/services';
import { OiqUiNotificationService } from '@oiq-ui-sdk/ui';
import {
  BehaviorSubject,
  combineLatest,
  from,
  interval,
  merge,
  Observable,
  of,
  Subject,
  Subscription,
  throwError,
} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  exhaustMap,
  filter,
  finalize,
  map,
  mergeMap,
  shareReplay,
  skip,
  skipWhile,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
  toArray,
} from 'rxjs/operators';
import { AppService } from 'src/app/app.service';
import {
  EmailAssociationsResponse,
  EmailError,
  EmailErrorAssociationsResponse,
  EmailLinkDto,
  EmailLinkTarget,
} from './email-app.interfaces';
import { ARCHIVE_LABELS, emailLinkKey, INBOX_LABELS, SENT_LABELS } from './email-app.utils';
import { EmailAppService } from './services/email-app.service';
import {
  Attachment,
  Email,
  EmailOAuthService,
  EmailProvider,
  EmailThread,
  PasswordAuthPayload,
  ThreadOthersResponse,
} from './services/email-oauth.service';
import { EmailPreviewService } from './services/email-preview.service';

export interface EmailFilters {
  labels?: string | null;
  pageToken?: string | null;
  query?: string | null;
}

export interface Label {
  id: string;
  name: string;
  type?: string;
}

export interface SendEmailAttachment {
  Filename: string;
  ContentType: string;
  Data: string; // Base64 encoded
  Size: number;
}

export interface SendEmailData {
  To: string[];
  Cc: string[];
  Subject: string;
  Body?: string;
  HTMLBody: string;
  Attachments?: SendEmailAttachment[];
}

export interface ReplyEmailData extends SendEmailData {
  includeOriginalMessage?: boolean;
  includeOriginalAttachments?: boolean;
}

// Reply All omits To/Cc — the backend derives recipients from the original message.
export interface ReplyAllEmailData extends Partial<SendEmailData> {
  IncludeOriginalMessage?: boolean;
  IncludeOriginalAttachments?: boolean;
}

export interface ForwardEmailData {
  To: string[];
  Cc?: string[];
  HTMLBody?: string;
  Attachments?: SendEmailAttachment[];
}

@Injectable()
export class EmailAppStore {
  private emailOAuthService = inject(EmailOAuthService);
  private emailAppService = inject(EmailAppService);
  private emailPreviewService = inject(EmailPreviewService);
  private router = inject(Router);
  private authService = inject(AuthService);
  private tenantService = inject(TenantService);
  private appService = inject(AppService);
  private notificationService = inject(OiqUiNotificationService);

  private _sharedMailbox = signal<string>('');
  private _isSharedMailboxConfirmed = signal<boolean>(false);

  public sharedMailbox = this._sharedMailbox;
  public isSharedMailboxConfirmed = this._isSharedMailboxConfirmed;

  setSharedMailbox(value: string) {
    this._sharedMailbox.set(value);

    if (!value) {
      this.setSharedMailboxConfirmed(false);
    }
  }

  setSharedMailboxConfirmed(value: boolean) {
    this._isSharedMailboxConfirmed.set(value);
  }

  // ===== INPUT STREAMS (BehaviorSubjects for imperative updates) =====
  private currentProvider$ = new BehaviorSubject<EmailProvider | null>(null);
  private emailFilters$ = new BehaviorSubject<EmailFilters>({
    labels: null,
    pageToken: null,
    query: null,
  });
  private reloadEmails$ = new Subject<void>();
  private reloadLabels$ = new Subject<void>();
  private reloadConnectedProviders$ = new Subject<void>();
  private reloadAssociations$ = new Subject<void>();
  private refreshNewEmails$ = new Subject<void>();
  private destroy$ = new Subject<void>();
  private periodicRefreshSubscription: Subscription | null = null;

  // ===== SIGNALS (Local State) =====
  private _selectedEmailId$ = new BehaviorSubject<string | null>(null);
  private _isOAuthInProgress = signal<boolean>(false);
  private _oauthProvider = signal<EmailProvider | null>(null);
  private _nextPageToken = signal<string | null>(null);
  private _hasMore = signal<boolean>(false);
  private _emailsList = signal<Email[]>([]);
  private _threadsList = signal<EmailThread[]>([]);
  // Cache of fully-loaded message content (HTMLBody, attachments) keyed by message ID
  private _messageContentCache = signal<Record<string, Email>>({});
  // Message IDs whose attachment payload is already being downloaded in the background
  // (kicked off by selectedEmail$). fetchMessageContent piggybacks on that request
  // instead of firing an identical one for the same message.
  private _attachmentsInFlight = new Set<string>();
  private _contentRequests = new Map<string, Observable<Email | null>>();
  // Threads whose `/thread/others` bodies are already merged in, so moving between
  // messages of one conversation never refetches them. The requests in flight are
  // keyed by message id, which is what the endpoint takes; the signal mirrors them
  // as thread ids for the message components to read.
  private _threadOthersLoaded = new Set<string>();
  private _threadOthersRequests = new Map<string, Observable<ThreadOthersResponse>>();
  private _threadOthersInFlight = signal<string[]>([]);

  // Provider to prefer excluding when auto-selecting after a logout
  private _excludedProvider: EmailProvider | null = null;

  private _initialized = false;

  // Email associations state
  private _emailAssociations = signal<EmailAssociationsResponse>({});

  // Per-message associations, keyed by message ID. Kept in their own map so a
  // thread-level reload (`loadAssociationsForThreads`) never wipes them.
  private _messageAssociations = signal<EmailAssociationsResponse>({});
  // Message IDs whose per-message association lookup is currently in flight
  private _messageAssociationsInFlight = new Set<string>();

  // Email ingestion errors state — keyed by individual message ID
  private _emailErrors = signal<EmailErrorAssociationsResponse>({});
  // Whether the ingestion-error lookup is in flight, and why it last failed. Without
  // these a failed lookup is indistinguishable from "no message has an error", because
  // both render as an absent warning indicator.
  private _isLoadingEmailErrors = signal<boolean>(false);
  private _emailErrorsError = signal<string | null>(null);

  // Client IDs by email addresses state
  private _clientIdsByEmails = signal<{ [email: string]: number[] }>({});

  // Refresh state
  private _isRefreshing = signal<boolean>(false);

  /**
   * Extract email address from formatted string like "Name <email@domain.com>" or just "email@domain.com"
   */
  private extractEmailAddress(emailString: string): string | null {
    if (!emailString) return null;

    // Match email address in angle brackets: Name <email@domain.com>
    const angleBracketMatch = emailString.match(/<([^>]+)>/);
    if (angleBracketMatch && angleBracketMatch[1]) {
      return angleBracketMatch[1].trim();
    }

    // Match standalone email address: email@domain.com
    const emailRegex = /[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/;
    const match = emailString.match(emailRegex);
    if (match && match[0]) {
      return match[0].trim();
    }

    return null;
  }

  // Tenant settings observable - checks for TwoWayEmail feature flag
  tenantSettings$: Observable<any> = this.authService.cookieChanges$.pipe(
    map(() => {
      const _user = this.authService.getUser();
      if (isInspectorUser(this.authService.userRoles)) {
        return null;
      }
      return _user && _user.tenantId ? _user.tenantId : null;
    }),
    skipWhile(tenantId => !tenantId),
    switchMap(tenantId =>
      tenantId ? this.tenantService.getSettings(this.authService.getUser().tenantId) : of({})
    ),
    shareReplay(1)
  );

  // Feature flag check - TwoWayEmail must be enabled AND user must have resource access
  isEmailAppEnabled$: Observable<boolean> = combineLatest([
    this.tenantSettings$,
    this.authService.cookieChanges$,
    this.appService.isMimicUser$,
  ]).pipe(
    map(([settings, _, _isImpersonated]) => {
      const hasFeatureFlag = !!settings?.features?.TwoWayEmails;
      const hasResourceAccess = this.authService.userHasAccessToResources('two-way-emails');
      const isEnabled = hasFeatureFlag && hasResourceAccess && !_isImpersonated;
      return isEnabled;
    }),
    shareReplay(1)
  );

  // Feature flag check - SharedMailbox
  isSharedMailboxEnabled$: Observable<boolean> = combineLatest([
    this.tenantSettings$,
    this.authService.cookieChanges$,
    this.appService.isMimicUser$,
  ]).pipe(
    map(([settings, _, _isImpersonated]) => {
      const hasFeatureFlag = !!settings?.features?.SharedMailbox;
      const isEnabled = hasFeatureFlag && !_isImpersonated;
      return isEnabled;
    }),
    shareReplay(1)
  );

  // Feature flag check - ArchiveEmails
  isArchiveEmailsEnabled$: Observable<boolean> = combineLatest([
    this.tenantSettings$,
    this.authService.cookieChanges$,
    this.appService.isMimicUser$,
  ]).pipe(
    map(([settings, _, _isImpersonated]) => {
      const hasFeatureFlag = !!settings?.features?.ArchiveEmails;
      const isEnabled = hasFeatureFlag && !_isImpersonated;
      return isEnabled;
    }),
    shareReplay(1)
  );

  // ===== DERIVED OBSERVABLES (Reactive Data Streams) =====

  // Connected providers from API
  connectedProviders$: Observable<any> = combineLatest([
    merge(of(null), this.reloadConnectedProviders$),
    this.authService.isLoggedIn,
    this.isEmailAppEnabled$,
  ]).pipe(
    filter(([_, isLoggedIn, isEnabled]) => isLoggedIn && isEnabled),
    debounceTime(50),
    switchMap(() => this.emailOAuthService.readUserConnectedProviders()),
    map(responseData => {
      // Decode JWT tokens and extract provider info (name, email)
      const providersInfo: any[] = [];
      if (responseData?.tokens && Array.isArray(responseData.tokens)) {
        for (const tokenData of responseData.tokens) {
          providersInfo.push({
            provider: tokenData.provider as EmailProvider, // Provider
            email: tokenData.email, // Email
          });
        }
      }

      // Enrich response with decoded provider info
      return {
        ...responseData,
        providersInfo: providersInfo,
      };
    }),
    tap(data => {
      if (!this._initialized) return;
      this.handleConnectedProvidersUpdate(data);
    }),
    catchError(error => {
      return of(null);
    }),
    shareReplay(1)
  );

  // Emails list
  emails$: Observable<Email[]> = combineLatest([
    merge(this.currentProvider$, this.emailFilters$, this.reloadEmails$),
    this.isEmailAppEnabled$,
    this.connectedProviders$,
  ]).pipe(
    filter(([_, isEnabled, providersData]) => {
      // Check if email app is enabled AND we have tokens
      const hasTokens =
        providersData?.tokens &&
        Array.isArray(providersData.tokens) &&
        providersData.tokens.length > 0;
      if (!isEnabled || !hasTokens) {
        return false;
      }
      return true;
    }),
    debounceTime(50),
    tap(() => {
      // Clear emails immediately when filters change to show loading state
      this._emailsList.set([]);
      this._nextPageToken.set(null);
      this._hasMore.set(false);
      this.emailPreviewService.closePngPreview();
    }),
    switchMap(() => {
      const provider = this.currentProvider$.value;
      const filters = this.emailFilters$.value;

      if (!provider) {
        return of([]);
      }

      const mailbox = this._sharedMailbox();
      return this.emailOAuthService
        .getEmails(
          provider,
          filters.labels || undefined,
          filters.pageToken || undefined,
          filters.query || undefined,
          mailbox ? mailbox : undefined
        )
        .pipe(
          tap(() => {
            if (mailbox) {
              this.setSharedMailboxConfirmed(true);
            }
          }),
          map((response: any) => {
            const threads = this.emailOAuthService.parseThreadsFromResponse(response);
            const emailsList = threads.map(t => t.Latest);

            // Update pagination state
            this._nextPageToken.set(response?.nextPageToken || null);
            this._hasMore.set(!!response?.nextPageToken);

            // Store both threads and the flat latest-message list (for back-compat consumers)
            this._threadsList.set(threads);
            this._emailsList.set(emailsList);

            // Fetch associations for the loaded threads
            if (threads.length > 0) {
              const threadIds = threads.map(t => t.ThreadID);
              this.loadAssociationsForThreads(threadIds);
              // Fetch ingestion errors for the loaded threads
              this.loadErrorsForThreads(threadIds);
            }

            return emailsList;
          }),
          catchError(error => {
            this._emailsList.set([]);
            this._nextPageToken.set(null);
            this._hasMore.set(false);
            if (mailbox) {
              this.setSharedMailbox('');
              this.reloadLabels();
              this.reloadEmails();
              this.router.navigate(['/v2/email-app'], { queryParams: { label: 'inbox' } });
            }
            return of([]);
          })
        );
    }),
    shareReplay(1)
  );

  // Email associations - fetched when threads are loaded or manually reloaded.
  // Cache is keyed by ThreadID. Backend `getAssociations` is called with thread IDs in the
  // `emailIds` parameter (param name preserved for back-compat).
  associations$: Observable<EmailAssociationsResponse> = combineLatest([
    merge(this.reloadAssociations$),
    this.isEmailAppEnabled$,
  ]).pipe(
    filter(([_, isEnabled]) => isEnabled),
    debounceTime(50),
    switchMap(() => {
      const threadsList = this._threadsList();
      const threadIds = threadsList.map(t => t.ThreadID);

      if (threadIds.length === 0) {
        return of({});
      }

      // Extract unique email addresses across all messages of all threads
      const emailAddresses = new Set<string>();
      threadsList.forEach(thread => {
        thread.Messages.forEach(m => {
          if (m.From) {
            const extracted = this.extractEmailAddress(m.From);
            if (extracted) emailAddresses.add(extracted);
          }
          if (m.To && Array.isArray(m.To)) {
            m.To.forEach(addr => {
              const extracted = this.extractEmailAddress(addr);
              if (extracted) emailAddresses.add(extracted);
            });
          }
        });
      });
      const uniqueEmailAddresses = Array.from(emailAddresses);

      // First load client IDs by emails, then load associations
      // TODO: Implement this properly once API is ready
      return this.emailAppService.getClientIdsByEmails([]).pipe(
        switchMap(clientIdsByEmails => {
          this._clientIdsByEmails.set(clientIdsByEmails);

          return this.emailAppService.getAssociations({ threadIds }).pipe(
            map(associations => {
              const mergedAssociations: EmailAssociationsResponse = { ...associations };

              // For each thread, fall back to client-IDs-by-email lookup using latest message's From
              threadsList.forEach(thread => {
                const threadId = thread.ThreadID;
                const extractedEmailAddress = this.extractEmailAddress(thread.Latest.From || '');

                if (
                  !mergedAssociations[threadId] &&
                  extractedEmailAddress &&
                  clientIdsByEmails[extractedEmailAddress]
                ) {
                  const clientIds = clientIdsByEmails[extractedEmailAddress];
                  if (clientIds && clientIds.length > 0) {
                    mergedAssociations[threadId] = clientIds.map(
                      clientId =>
                        ({
                          emailId: null,
                          threadId,
                          clientId,
                        }) as EmailLinkDto
                    );
                  }
                } else if (
                  mergedAssociations[threadId] &&
                  extractedEmailAddress &&
                  clientIdsByEmails[extractedEmailAddress]
                ) {
                  const existingAssociations = mergedAssociations[threadId];
                  const clientIds = clientIdsByEmails[extractedEmailAddress];

                  existingAssociations.forEach(assoc => {
                    if (!assoc.clientId && clientIds && clientIds.length > 0) {
                      assoc.clientId = clientIds[0];
                    }
                  });
                }
              });

              this._emailAssociations.set(mergedAssociations);
              return mergedAssociations;
            }),
            catchError(() => of({}))
          );
        }),
        catchError(() => {
          return this.emailAppService.getAssociations({ threadIds }).pipe(
            tap(associations => {
              this._emailAssociations.set(associations);
            }),
            catchError(() => of({}))
          );
        })
      );
    }),
    shareReplay(1)
  );

  // Labels for current provider
  labels$: Observable<Label[]> = combineLatest([
    merge(this.currentProvider$, this.reloadLabels$),
    this.isEmailAppEnabled$,
    this.connectedProviders$,
  ]).pipe(
    filter(([_, isEnabled, providersData]) => {
      // Check if email app is enabled AND we have tokens
      const hasTokens =
        providersData?.tokens &&
        Array.isArray(providersData.tokens) &&
        providersData.tokens.length > 0;
      if (!isEnabled || !hasTokens) {
        return false;
      }
      return true;
    }),
    debounceTime(50),
    switchMap(() => {
      const provider = this.currentProvider$.value;

      if (!provider) {
        return of([]);
      }

      const mailbox = this._sharedMailbox();
      return this.emailOAuthService.getLabels(provider, mailbox ? mailbox : undefined).pipe(
        tap(() => {
          if (mailbox) {
            this.setSharedMailboxConfirmed(true);
          }
        }),
        catchError(error => {
          if (mailbox) {
            this.setSharedMailbox('');
            this.reloadLabels();
            this.reloadEmails();
            this.router.navigate(['/v2/email-app'], { queryParams: { label: 'inbox' } });
          }
          return of([]);
        })
      );
    }),
    shareReplay(1)
  );

  // Observable from the service's viewedEmail signal (created at class level for injection context)
  private viewedEmailUpdates$: Observable<Email | null> = toObservable(
    this.emailOAuthService.viewedEmail
  );

  // Selected email content
  // Merge the initial fetch with updates from the service's viewedEmail signal
  // This ensures that when attachments are lazy-loaded and update _viewedEmail,
  // the selectedEmail$ observable also emits the updated email with attachments
  selectedEmail$: Observable<Email | null> = combineLatest([
    this._selectedEmailId$,
    this.currentProvider$,
    this.isEmailAppEnabled$,
  ]).pipe(
    filter(([_, __, isEnabled]) => isEnabled),
    tap(() => {
      this.emailPreviewService.closePngPreview();
    }),
    distinctUntilChanged(([prevId], [currId]) => prevId === currId),
    switchMap(([emailId, provider]) => {
      if (!emailId || !provider) {
        return of(null);
      }

      // First mark email as read, then fetch content
      return this.markEmailAsRead(emailId, true).pipe(
        catchError(() => of(undefined)),
        switchMap(() => {
          // Fetch email content without attachments first (faster initial load)
          const mailbox = this._sharedMailbox();

          // Pull the rest of the thread's bodies alongside the content request.
          this.loadThreadOthers(emailId);

          const initialEmail$ = this.emailOAuthService
            .getEmailContent(provider, emailId, false, mailbox ? mailbox : undefined)
            .pipe(
              startWith(null),
              tap(email => {
                // After email content loads, fetch attachments in the background
                if (email && email.ID) {
                  const messageId = email.ID;
                  this._attachmentsInFlight.add(messageId);
                  this.emailOAuthService
                    .getEmailAttachments(provider, messageId, mailbox ? mailbox : undefined)
                    .subscribe({
                      next: () => this._attachmentsInFlight.delete(messageId),
                      error: () => {
                        // Don't show error to user, attachments will just be empty
                        this._attachmentsInFlight.delete(messageId);
                      },
                    });
                }
              }),
              catchError(() => {
                return of(null);
              })
            );

          // Also listen to the service's viewedEmail signal to get updates when attachments are loaded
          // Filter to only emit when the viewed email matches the selected emailId
          const filteredViewedEmailUpdates$ = this.viewedEmailUpdates$.pipe(
            filter((email): email is Email => email !== null && email.ID === emailId)
          );

          // Merge initial fetch with subsequent updates from viewedEmail signal
          return merge(initialEmail$, filteredViewedEmailUpdates$).pipe(
            // Sync the freshly loaded content into _threadsList and the per-message
            // cache. Without this, email-thread-message's lazy-load effect would
            // see a thread message with no HTMLBody and fire a second
            // fetchMessageContent for content we just downloaded.
            tap(email => {
              if (email && email.ID) {
                this.replaceMessageInThread(email);
                this.cacheMessageContent(email);
              }
            }),
            // Use distinctUntilChanged to avoid duplicate emissions
            distinctUntilChanged((prev, curr) => {
              // Compare email IDs and attachment counts to detect meaningful changes
              if (!prev && !curr) return true;
              if (!prev || !curr) return false;
              if (prev.ID !== curr.ID) return false;
              const prevAttachments = prev.Attachments?.length || 0;
              const currAttachments = curr.Attachments?.length || 0;
              // If attachments count changed, it's a meaningful update
              if (prevAttachments !== currAttachments) return false;
              // If attachments exist, check if they have Data property
              if (prevAttachments > 0 && currAttachments > 0) {
                const prevHasData = prev.Attachments?.some(att => !!att.Data) || false;
                const currHasData = curr.Attachments?.some(att => !!att.Data) || false;
                if (prevHasData !== currHasData) return false;
              }
              return true;
            })
          );
        })
      );
    }),
    shareReplay(1)
  );

  // ===== SIGNALS FROM OBSERVABLES (toSignal conversion) =====
  connectedProvidersData = toSignal(this.connectedProviders$, { initialValue: null });
  labels = toSignal(this.labels$, { initialValue: [] });
  selectedEmail = toSignal(this.selectedEmail$, { initialValue: null });
  currentProvider = toSignal(this.currentProvider$, { initialValue: null });
  activeLabels = toSignal(this.emailFilters$.pipe(map(f => f.labels ?? '')), { initialValue: '' });
  currentAccount = computed(() => this.emailOAuthService.currentAccount());

  // Feature flag archive emails signal
  isArchiveEmailsEnabled = toSignal(this.isArchiveEmailsEnabled$, { initialValue: false });
  // Feature flag shared mailbox signal
  isSharedMailboxEnabled = toSignal(this.isSharedMailboxEnabled$, { initialValue: false });
  activeLabel = toSignal(
    this.emailFilters$.pipe(
      distinctUntilChanged((a, b) => a.labels === b.labels),
      map(filters => {
        if (!filters.labels) return null;
        const labels = this.labels();
        return (
          labels.find(l => l.id === filters.labels) ??
          ({
            id: filters.labels,
            name: filters.labels,
          } as Label)
        );
      })
    ),
    { initialValue: null }
  );

  activeLabelKey = computed(() => {
    const activeLabel = this.activeLabel();
    return activeLabel ? activeLabel.name.toLowerCase().replace(/\s+/g, '') : null;
  });

  activeLabelId = computed(() => {
    const activeLabel = this.activeLabel();
    return activeLabel ? activeLabel.id : null;
  });

  // Computed signal to determine if the active label is a sent label
  isActiveSentLabel = computed(() => SENT_LABELS.includes(this.activeLabelKey() ?? ''));

  // Computed signal to determine if the active label is a archive label
  isActiveArchiveLabel = computed(() => ARCHIVE_LABELS.includes(this.activeLabelKey() ?? ''));

  // Computed signal to determine if the active label is a inbox label
  isActiveInboxLabel = computed(() => INBOX_LABELS.includes(this.activeLabelKey() ?? ''));

  // Subscribe to emails$ to trigger initial loads, but use _emailsList for the actual data
  private _emailsSubscription = toSignal(this.emails$, { initialValue: [] });

  // Subscribe to associations$ to trigger loads
  private _associationsSubscription = toSignal(this.associations$, { initialValue: {} });

  // Emails signal - use the internal signal that gets updated by both initial load and loadMore
  emails = computed(() => this._emailsList());

  // Threads signal — same data, grouped by ThreadID with all messages preserved
  threads = computed(() => this._threadsList());

  // Cache of fully-loaded message content (HTMLBody, attachments) keyed by message ID
  messageContentCache = computed(() => this._messageContentCache());

  // Email associations signal
  emailAssociations = computed(() => this._emailAssociations());

  // Per-message associations signal — read-only client/application info per message
  messageAssociations = computed(() => this._messageAssociations());

  // Email ingestion errors signal — keyed by individual message ID
  emailErrors = computed(() => this._emailErrors());

  // Ingestion-error lookup state. `emailErrorsError` being set means the warning
  // indicators on screen cannot be trusted to be complete.
  isLoadingEmailErrors = computed(() => this._isLoadingEmailErrors());
  emailErrorsError = computed(() => this._emailErrorsError());

  // Client IDs by emails signal
  clientIdsByEmails = computed(() => this._clientIdsByEmails());

  // Is refreshing signal
  isRefreshing = computed(() => this._isRefreshing());

  // ===== COMPUTED SIGNALS (Derived State) =====

  isAuthenticated = computed(() => {
    const providersData = this.connectedProvidersData();
    return providersData?.tokens && providersData.tokens.length > 0;
  });

  authenticatedProviders = computed(() => {
    const providersData = this.connectedProvidersData();
    if (providersData?.tokens && Array.isArray(providersData.tokens)) {
      return providersData.tokens.map((token: any) => token.provider as EmailProvider);
    }
    return [];
  });

  hasEmails = computed(() => this.emails().length > 0);

  isLoading = computed(() => this.emailOAuthService.isLoading());
  error = computed(() => this.emailOAuthService.error());

  isLoadingEmailContent = computed(() => this.emailOAuthService.isLoadingEmailContent());
  emailContentError = computed(() => this.emailOAuthService.emailContentError());
  isLoadingAttachments = computed(() => this.emailOAuthService.isLoadingAttachments());
  attachmentsError = computed(() => this.emailOAuthService.attachmentsError());

  isOAuthInProgress = computed(() => this._isOAuthInProgress());
  oauthProvider = computed(() => this._oauthProvider());

  // Pagination signals
  nextPageToken = computed(() => this._nextPageToken());
  hasMore = computed(() => this._hasMore());

  // ===== CONSTRUCTOR =====
  constructor() {
    // Check OAuth status on initialization
    this.checkOAuthStatus();

    this.authService.isLoggedIn
      .pipe(
        distinctUntilChanged(),
        skip(1),
        filter(isLoggedIn => !isLoggedIn),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.resetStore();
      });

    this.authService.cookieChanges$
      .pipe(
        map((cookie: any) => cookie?.id ?? null),
        distinctUntilChanged(),
        skip(1),
        filter(id => id !== null),
        takeUntil(this.destroy$)
      )
      .subscribe(() => {
        this.resetStore();
      });
  }

  /**
   * Set up periodic refresh to check for new emails every 30 seconds
   */
  private setupPeriodicRefresh(): void {
    this.periodicRefreshSubscription = combineLatest([
      this.currentProvider$,
      this.isEmailAppEnabled$,
      this.connectedProviders$,
    ])
      .pipe(
        switchMap(([provider, isEnabled, providersData]) => {
          // Check if we should enable periodic refresh
          const hasTokens =
            providersData?.tokens &&
            Array.isArray(providersData.tokens) &&
            providersData.tokens.length > 0;
          const shouldRefresh = isEnabled && !!provider && hasTokens;

          if (!shouldRefresh) {
            // Return empty observable if conditions not met
            return of(null);
          }

          // Return interval that triggers refresh every 30 seconds
          return interval(30000).pipe(
            // Use exhaustMap to prevent overlapping refresh calls
            // If a refresh is in progress, skip the next interval tick
            exhaustMap(() => {
              // refreshNewEmails already checks isEmailAppEnabled internally,
              // but we've already checked it here, so we can pass a flag to skip the check
              return this.refreshNewEmails(true);
            }),
            catchError(() => {
              return of([]);
            })
          );
        }),
        takeUntil(this.destroy$)
      )
      .subscribe();
  }

  startPeriodicRefresh(): void {
    if (!this.periodicRefreshSubscription) {
      this.setupPeriodicRefresh();
    }
  }

  stopPeriodicRefresh(): void {
    if (this.periodicRefreshSubscription) {
      this.periodicRefreshSubscription.unsubscribe();
      this.periodicRefreshSubscription = null;
    }
  }

  // ===== ACTION METHODS =====

  initialize(): void {
    if (this._initialized) return;
    this._initialized = true;

    this.connectedProviders$
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(data => this.handleConnectedProvidersUpdate(data));
  }

  private handleConnectedProvidersUpdate(data: any): void {
    if (!this._initialized) return;

    const hasTokens = data?.tokens && data.tokens.length > 0;

    if (!hasTokens) {
      const path = this.router.url.split('?')[0];
      if (path.startsWith('/v2/email-app')) {
        this.router.navigate(['/']);
      }
      return;
    }

    if (this.currentProvider$.value) return;

    const candidateToken = data.tokens.find(
      (t: any) => (t.provider as EmailProvider) !== this._excludedProvider
    );
    const selectedToken = candidateToken || data.tokens[0];
    this.currentProvider$.next(selectedToken.provider as EmailProvider);
    this.emailOAuthService.setCurrentAccount(selectedToken.email || selectedToken.account || null);
    this._excludedProvider = null;
  }

  /**
   * Set the current active provider
   */
  setCurrentProvider(provider: EmailProvider, account?: string): void {
    // Always clear selected email and reset inbox state when switching providers
    this._selectedEmailId$.next(null);
    this._emailsList.set([]);
    this._threadsList.set([]);
    this._messageContentCache.set({});
    this._attachmentsInFlight.clear();
    this._contentRequests.clear();
    this._threadOthersLoaded.clear();
    this._threadOthersRequests.clear();
    this._threadOthersInFlight.set([]);
    this._nextPageToken.set(null);
    this._hasMore.set(false);
    this._emailAssociations.set({});
    this._messageAssociations.set({});
    this._messageAssociationsInFlight.clear();
    this._emailErrors.set({});
    this._emailErrorsError.set(null);
    this._isLoadingEmailErrors.set(false);

    // Reset email filters to default (labels/pageToken from previous provider are stale)
    this.emailFilters$.next({ labels: null, pageToken: null, query: null });

    // Navigate away from child routes (email view or compose) back to inbox
    const currentPath = this.router.url.split('?')[0];
    if (currentPath.startsWith('/v2/email-app/') && currentPath !== '/v2/email-app') {
      this.router.navigate(['/v2/email-app'], { queryParams: { label: 'inbox' } });
    }

    // Clear shared mailbox when switching providers
    this.setSharedMailbox('');

    // Set the new provider and account (this will trigger email reload)
    this.currentProvider$.next(provider);
    this.emailOAuthService.setCurrentAccount(account || null);
  }

  /**
   * Update email filters (labels, pageToken)
   */
  setEmailFilters(filters: Partial<EmailFilters>): void {
    const currentFilters = this.emailFilters$.value;
    this.emailFilters$.next({ ...currentFilters, ...filters });
  }

  /**
   * Select an email to view its content
   */
  selectEmail(emailId: string | null): void {
    this._selectedEmailId$.next(emailId);
  }

  /**
   * Clear selected email
   */
  clearSelectedEmail(): void {
    this._selectedEmailId$.next(null);
  }

  /**
   * Mark an email as read
   */
  markEmailAsRead(emailId: string, read: boolean, skipUpdate: boolean = true): Observable<void> {
    const currentEmails = this._emailsList();
    const email = currentEmails.find(e => e.ID === emailId);
    const mailbox = this._sharedMailbox();

    // If email is already read, don't send the request
    if (email?.Read && skipUpdate) {
      return of(undefined);
    }

    // Optimistically update the UI — update both the flat emails list and the thread message
    const updatedEmails = currentEmails.map(email =>
      email.ID === emailId ? { ...email, Read: read } : email
    );
    this._emailsList.set(updatedEmails);

    const currentThreads = this._threadsList();
    const threadIdx = currentThreads.findIndex(t => t.Messages.some(m => m.ID === emailId));
    if (threadIdx !== -1) {
      const t = currentThreads[threadIdx];
      const newMessages = t.Messages.map(m => (m.ID === emailId ? { ...m, Read: read } : m));
      const newList = [...currentThreads];
      newList[threadIdx] = this.rebuildThread(t.ThreadID, newMessages);
      this._threadsList.set(newList);
    }

    // Call the API to update the flag
    return this.emailOAuthService
      .updateEmailMessageFlags(emailId, { read }, mailbox ? mailbox : undefined)
      .pipe(
        catchError(() => {
          // Revert the optimistic update on error
          this._emailsList.set(currentEmails);
          this._threadsList.set(currentThreads);
          return of(undefined);
        })
      );
  }

  reloadEmails(): void {
    this.reloadEmails$.next();
  }

  /**
   * Refresh new emails - fetches latest emails and prepends new ones to the existing list
   * This is used for periodic refresh without clearing the current list
   * @param skipEnabledCheck - If true, skip the isEmailAppEnabled$ check (used when called from setupPeriodicRefresh)
   */
  refreshNewEmails(skipEnabledCheck: boolean = false): Observable<Email[]> {
    const provider = this.currentProvider$.value;
    const filters = this.emailFilters$.value;
    const mailbox = this._sharedMailbox();
    const isSharedMailboxConfirmed = this._isSharedMailboxConfirmed();

    // Don't refresh if already refreshing (exhaustMap should prevent this, but double-check)
    if (this._isRefreshing()) {
      return of([]);
    }

    if (!provider) {
      return of([]);
    }

    // Check if email app is enabled (unless skipEnabledCheck is true)
    const enabledCheck$ = skipEnabledCheck
      ? of(true)
      : this.isEmailAppEnabled$.pipe(filter(isEnabled => isEnabled));

    return enabledCheck$.pipe(
      switchMap(() => {
        // Set refreshing flag
        this._isRefreshing.set(true);

        // Fetch latest emails WITHOUT pageToken (gets newest first)
        return this.emailOAuthService
          .getEmails(
            provider,
            filters.labels || undefined,
            undefined, // No pageToken - get latest emails
            filters.query || undefined,
            mailbox && isSharedMailboxConfirmed ? mailbox : undefined
          )
          .pipe(
            map((response: any) => {
              const fetchedThreads: EmailThread[] =
                this.emailOAuthService.parseThreadsFromResponse(response);

              // Merge threads — replace existing threads (since they may have new messages)
              // and prepend genuinely new ones
              const currentThreads = this._threadsList();
              const currentByThreadId = new Map(currentThreads.map(t => [t.ThreadID, t]));

              let newOrUpdatedThreads = false;
              const newEmails: Email[] = [];
              const changedThreadIds: string[] = [];

              for (const t of fetchedThreads) {
                const existing = currentByThreadId.get(t.ThreadID);
                if (!existing) {
                  newOrUpdatedThreads = true;
                  newEmails.push(t.Latest);
                  changedThreadIds.push(t.ThreadID);
                  currentByThreadId.set(t.ThreadID, t);
                } else if (
                  existing.MessageCount !== t.MessageCount ||
                  existing.Latest.ID !== t.Latest.ID
                ) {
                  newOrUpdatedThreads = true;
                  newEmails.push(t.Latest);
                  changedThreadIds.push(t.ThreadID);
                  currentByThreadId.set(t.ThreadID, t);
                  // The refreshed copy comes back with empty bodies and a message we
                  // have never fetched, so let the thread pull its bodies again.
                  this._threadOthersLoaded.delete(t.ThreadID);
                }
              }

              if (newOrUpdatedThreads) {
                const mergedThreads = Array.from(currentByThreadId.values()).sort(
                  (a, b) => new Date(b.LatestDate).getTime() - new Date(a.LatestDate).getTime()
                );
                this._threadsList.set(mergedThreads);
                this._emailsList.set(mergedThreads.map(t => t.Latest));

                // Update nextPageToken if we got one (for pagination continuity)
                if (response?.nextPageToken) {
                  this._nextPageToken.set(response.nextPageToken);
                  this._hasMore.set(true);
                }

                // Fetch associations for new/updated threads
                if (changedThreadIds.length > 0) {
                  this.loadAssociationsForThreads(changedThreadIds);
                }
              }

              // Re-check ingestion errors for every loaded thread on each tick, not only
              // the changed ones. A message that was re-processed successfully keeps the
              // same MessageCount and Latest.ID, so its thread never lands in
              // changedThreadIds — asking for all of them lets the backend tell us what
              // is still failing, and `loadErrorsForThreads` drops whatever it omits.
              this.loadErrorsForThreads(Array.from(currentByThreadId.keys()), true);

              return newOrUpdatedThreads ? newEmails : [];
            }),
            // Use finalize to ensure _isRefreshing is reset even on errors
            finalize(() => {
              this._isRefreshing.set(false);
            }),
            catchError(() => {
              return of([]);
            })
          );
      }),
      catchError(() => {
        this._isRefreshing.set(false);
        return of([]);
      })
    );
  }

  reloadLabels(): void {
    this.reloadLabels$.next();
  }

  reloadConnectedProviders(): void {
    this.reloadConnectedProviders$.next();
  }

  /**
   * Clear the current provider and reset provider-related UI state.
   * Use this after disconnecting a provider so the UI shows an empty state immediately.
   */
  clearCurrentProvider(exclude?: EmailProvider): void {
    this.currentProvider$.next(null);
    this.emailOAuthService.setCurrentAccount(null);
    this._emailsList.set([]);
    this._threadsList.set([]);
    this._messageContentCache.set({});
    this._attachmentsInFlight.clear();
    this._contentRequests.clear();
    this._threadOthersLoaded.clear();
    this._threadOthersRequests.clear();
    this._threadOthersInFlight.set([]);
    this._nextPageToken.set(null);
    this._hasMore.set(false);
    this._emailAssociations.set({});
    this._messageAssociations.set({});
    this._messageAssociationsInFlight.clear();
    this._emailErrors.set({});
    this._emailErrorsError.set(null);
    this._isLoadingEmailErrors.set(false);
    this._selectedEmailId$.next(null);
    this._excludedProvider = exclude || null;
    this.emailFilters$.next({ labels: null, pageToken: null, query: null });
    this.setSharedMailbox('');
  }

  startOAuthFlow(provider: EmailProvider): void {
    this._isOAuthInProgress.set(true);
    this._oauthProvider.set(provider);
    this.emailOAuthService.startOAuthFlow(provider);
  }

  connectWithPassword(email: string, password: string): Observable<any> {
    const payload: PasswordAuthPayload = {
      provider: 'gmail_imap',
      email,
      password,
    };

    this._isOAuthInProgress.set(true);
    this._oauthProvider.set('gmail_imap');

    return this.emailOAuthService.connectWithPassword(payload).pipe(
      tap(() => {
        this._isOAuthInProgress.set(false);
        this._oauthProvider.set(null);
        this.reloadConnectedProviders();
      }),
      catchError(error => {
        this._isOAuthInProgress.set(false);
        this._oauthProvider.set(null);
        return throwError(() => error);
      })
    );
  }

  handleOAuthCallback(code: string, state: string, provider: EmailProvider): Observable<any> {
    return from(this.emailOAuthService.handleOAuthCallback(code, state, provider)).pipe(
      tap(() => {
        this._isOAuthInProgress.set(false);
        this._oauthProvider.set(null);
        // Reload connected providers after successful OAuth
        this.reloadConnectedProviders();
      }),
      catchError(error => {
        this._isOAuthInProgress.set(false);
        this._oauthProvider.set(null);
        return throwError(() => error);
      })
    );
  }

  sendEmail(data: SendEmailData): Observable<any> {
    const provider = this.currentProvider$.value;
    if (!provider) {
      return throwError(() => new Error('No provider selected'));
    }
    const mailbox = this._sharedMailbox();

    return this.emailOAuthService.sendEmail(provider, data, mailbox ? mailbox : undefined).pipe(
      tap(() => {
        this.reloadEmails();
      })
    );
  }

  replyEmail(emailId: string, data: ReplyEmailData): Observable<any> {
    const provider = this.currentProvider$.value;
    if (!provider) {
      return throwError(() => new Error('No provider selected'));
    }
    const mailbox = this._sharedMailbox();

    return this.emailOAuthService
      .replyEmail(provider, emailId, data, mailbox ? mailbox : undefined)
      .pipe(
        tap(() => {
          this.reloadEmails();
        })
      );
  }

  replyToAllEmail(emailId: string, data: ReplyAllEmailData): Observable<any> {
    const provider = this.currentProvider$.value;
    if (!provider) {
      return throwError(() => new Error('No provider selected'));
    }
    const mailbox = this._sharedMailbox();

    return this.emailOAuthService
      .replyToAllEmail(provider, emailId, data, mailbox ? mailbox : undefined)
      .pipe(
        tap(() => {
          this.reloadEmails();
        })
      );
  }

  forwardEmail(emailId: string, data: ForwardEmailData): Observable<any> {
    const provider = this.currentProvider$.value;
    if (!provider) {
      return throwError(() => new Error('No provider selected'));
    }
    const mailbox = this._sharedMailbox();

    return this.emailOAuthService
      .forwardEmail(provider, emailId, data, mailbox ? mailbox : undefined)
      .pipe(
        tap(() => {
          this.reloadEmails();
        })
      );
  }

  loadMoreEmails(): Observable<any> {
    const provider = this.currentProvider$.value;
    const nextToken = this._nextPageToken();
    const filters = this.emailFilters$.value;
    const mailbox = this._sharedMailbox();
    const isSharedMailboxConfirmed = this._isSharedMailboxConfirmed();

    if (!provider || !nextToken) {
      return of(null);
    }

    // Check if email app is enabled before loading more

    return this.emailOAuthService
      .getEmails(
        provider,
        filters.labels || undefined,
        nextToken,
        filters.query || undefined,
        mailbox && isSharedMailboxConfirmed ? mailbox : undefined
      )
      .pipe(
        map((response: any) => {
          const newThreads: EmailThread[] =
            this.emailOAuthService.parseThreadsFromResponse(response);

          // Append new threads (dedup by ThreadID; replace if existing has fewer messages)
          const currentThreads = this._threadsList();
          const currentByThreadId = new Map(currentThreads.map(t => [t.ThreadID, t]));
          const newEmails: Email[] = [];
          const newThreadIds: string[] = [];

          for (const t of newThreads) {
            const existing = currentByThreadId.get(t.ThreadID);
            if (!existing) {
              currentByThreadId.set(t.ThreadID, t);
              newEmails.push(t.Latest);
              newThreadIds.push(t.ThreadID);
            } else if (t.MessageCount > existing.MessageCount) {
              currentByThreadId.set(t.ThreadID, t);
            }
          }

          const mergedThreads = Array.from(currentByThreadId.values()).sort(
            (a, b) => new Date(b.LatestDate).getTime() - new Date(a.LatestDate).getTime()
          );
          this._threadsList.set(mergedThreads);
          this._emailsList.set(mergedThreads.map(t => t.Latest));

          // Update pagination state
          this._nextPageToken.set(response?.nextPageToken || null);
          this._hasMore.set(!!response?.nextPageToken);

          // Fetch associations for the new threads
          if (newThreadIds.length > 0) {
            this.loadAssociationsForThreads(newThreadIds);
            // Fetch ingestion errors for the new threads
            this.loadErrorsForThreads(newThreadIds, true);
          }

          return response;
        }),
        catchError(() => {
          return of(null);
        })
      );
  }

  /**
   * Link a thread (or a standalone message, for providers without threads) to a client.
   */
  linkEmailToClient(target: EmailLinkTarget, clientId: number): Observable<EmailLinkDto> {
    return this.emailAppService.linkClient(target, clientId).pipe(
      tap(linkDto => {
        this.updateAssociationInStore(target, linkDto);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Link a thread to an application.
   */
  linkEmailToApplication(target: EmailLinkTarget, applicationId: number): Observable<EmailLinkDto> {
    return this.emailAppService.linkApplication(target, applicationId).pipe(
      tap(linkDto => {
        this.updateAssociationInStore(target, linkDto);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Unlink a thread from a client (and its application).
   */
  unlinkEmailFromClient(target: EmailLinkTarget, clientId: number): Observable<EmailLinkDto> {
    return this.emailAppService.unlinkClient(target, clientId).pipe(
      tap(() => {
        this.updateAssociationInStore(target, null);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Unlink a thread from an application.
   */
  unlinkEmailFromApplication(
    target: EmailLinkTarget,
    clientId: number,
    applicationId: number
  ): Observable<EmailLinkDto> {
    return this.emailAppService.unlinkApplication(target, clientId, applicationId).pipe(
      tap(linkDto => {
        this.updateAssociationInStore(target, linkDto);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Delete an email
   */
  deleteEmail(emailId: string): Observable<void> {
    const provider = this.currentProvider$.value;
    if (!provider) {
      return throwError(() => new Error('No provider selected'));
    }
    const mailbox = this._sharedMailbox();

    const currentEmails = this._emailsList();
    const selectedEmailId = this._selectedEmailId$.value;

    const currentThreads = this._threadsList();

    return this.emailOAuthService
      .deleteEmail(provider, emailId, mailbox ? mailbox : undefined)
      .pipe(
        tap(() => {
          // Decide how to update local state based on the size of the thread the
          // message belongs to. For a single-message thread we remove it from the
          // list and drop the (now empty) thread. For a multi-message thread we
          // leave both the list and the thread untouched — only the server delete
          // happens.
          const tIdx = currentThreads.findIndex(t => t.Messages.some(m => m.ID === emailId));
          const isSingleMessageThread = tIdx === -1 || currentThreads[tIdx].Messages.length <= 1;

          if (isSingleMessageThread) {
            const updatedEmails = currentEmails.filter(email => email.ID !== emailId);
            this._emailsList.set(updatedEmails);

            if (tIdx !== -1) {
              const newList = [...currentThreads];
              newList.splice(tIdx, 1);
              this._threadsList.set(newList);
            }
          }

          // If the deleted email was selected, clear the selection
          if (selectedEmailId === emailId) {
            this._selectedEmailId$.next(null);

            // Navigate away from email view route if we're on it
            const currentPath = this.router.url.split('?')[0];
            if (currentPath.startsWith('/v2/email-app/') && currentPath !== '/v2/email-app') {
              const urlTree = this.router.parseUrl(this.router.url);
              const queryParams = urlTree.queryParams;
              this.router.navigate(['/v2/email-app'], { queryParams });
            }
          }
        }),
        catchError(error => {
          // Revert the optimistic update on error
          this._emailsList.set(currentEmails);
          this._threadsList.set(currentThreads);
          if (selectedEmailId === emailId) {
            this._selectedEmailId$.next(emailId);
          }
          throw error;
        }),
        map(() => void 0)
      );
  }

  /**
   * Link a thread to a client and optionally an application.
   */
  linkEmailToClientAndApplication(
    target: EmailLinkTarget,
    clientId: number,
    applicationId?: number
  ): Observable<EmailLinkDto> {
    return this.emailAppService.linkClientAndApplication(target, clientId, applicationId).pipe(
      tap(linkDto => {
        this.updateAssociationInStore(target, linkDto);
      }),
      catchError(error => {
        throw error;
      })
    );
  }

  /**
   * Get the association for a specific thread (or standalone message).
   */
  getEmailAssociation(target: EmailLinkTarget): EmailLinkDto | null {
    const key = emailLinkKey(target);
    if (!key) return null;

    const associations = this._emailAssociations();
    const threadAssociations = associations[key];
    return threadAssociations && threadAssociations.length > 0 ? threadAssociations[0] : null;
  }

  /**
   * Get the ingestion errors for a specific email message (keyed by message ID).
   * Returns an empty array when the message has no errors.
   */
  getEmailErrors(emailId: string): EmailError[] {
    return this._emailErrors()[emailId] ?? [];
  }

  /**
   * Association resolved for a single message. Populated by `loadAssociationsForMessages`,
   * which runs when a thread is opened — the backend migration moves links onto individual
   * messages, so a message can carry a client/application of its own.
   */
  getMessageAssociation(messageId: string): EmailLinkDto | null {
    if (!messageId) return null;

    const messageAssociations = this._messageAssociations()[messageId];
    return messageAssociations && messageAssociations.length > 0 ? messageAssociations[0] : null;
  }

  /**
   * Load associations for individual messages, keyed by message ID. Called with every
   * message of a thread the moment that thread is opened. Results live in their own map,
   * so a thread-level reload never overwrites them.
   *
   * Messages already resolved, or with a lookup in flight, are skipped; pass `force`
   * to re-read them (e.g. after a link/unlink).
   */
  loadAssociationsForMessages(messageIds: string[], force = false): void {
    const known = this._messageAssociations();
    const emailIds = Array.from(new Set(messageIds.filter(Boolean))).filter(
      id => !this._messageAssociationsInFlight.has(id) && (force || !(id in known))
    );
    if (emailIds.length === 0) return;

    emailIds.forEach(id => this._messageAssociationsInFlight.add(id));

    this.emailAppService
      .getAssociations({ emailIds })
      .pipe(finalize(() => emailIds.forEach(id => this._messageAssociationsInFlight.delete(id))))
      .subscribe({
        next: associations => {
          // Record every requested id, including the ones that came back empty — that
          // negative result is what keeps the next thread open from re-asking for them.
          const resolved: EmailAssociationsResponse = {};
          emailIds.forEach(id => (resolved[id] = associations[id] ?? []));
          this._messageAssociations.set({ ...this._messageAssociations(), ...resolved });
        },
        error: () => {},
      });
  }

  /**
   * Find a thread by ID, or by any contained message ID. Returns null if not in the loaded list.
   */
  getThreadByEmailId(idOrThreadId: string): EmailThread | null {
    if (!idOrThreadId) return null;
    const threads = this._threadsList();
    return (
      threads.find(t => t.ThreadID === idOrThreadId) ||
      threads.find(t => t.Messages.some(m => m.ID === idOrThreadId)) ||
      null
    );
  }

  /**
   * Lazy-fetch a single message's full content (HTMLBody + attachments) and cache it.
   * A cached entry short-circuits the network call only when it is complete —
   * the initial `selectedEmail$` fetch runs with includeAttachments=false, so its
   * cached payload carries attachment metadata without the base64 `Data` and must
   * not be served as final.
   */
  fetchMessageContent(messageId: string): Observable<Email | null> {
    if (!messageId) return of(null);

    const cached = this._messageContentCache()[messageId];
    if (cached && (this.isContentComplete(cached) || this._attachmentsInFlight.has(messageId))) {
      // Complete, or the missing attachment data is already on its way — consumers
      // read the cache reactively, so the late update reaches them either way.
      return of(cached);
    }

    return this.requestMessageContent(messageId);
  }

  /**
   * Pull the bodies for a whole thread in one call and fold them into the loaded
   * thread, replacing the `/content` request each message would otherwise need.
   *
   * Addressed by message id but deduplicated per thread. On a deep link the thread
   * isn't in the list yet, so that check can only happen once the response names it.
   * Failures are not remembered, so a later visit retries.
   */
  fetchThreadOthers(emailId: string): Observable<ThreadOthersResponse> {
    const provider = this.currentProvider$.value;
    const empty: ThreadOthersResponse = { threadId: '', messages: [] };
    if (!emailId || !provider) return of(empty);

    const knownThreadId = this.getThreadByEmailId(emailId)?.ThreadID;
    if (knownThreadId && this._threadOthersLoaded.has(knownThreadId)) return of(empty);

    const inFlight = this._threadOthersRequests.get(emailId);
    if (inFlight) return inFlight;

    const mailbox = this._sharedMailbox();
    if (knownThreadId) this.markThreadOthersInFlight(knownThreadId, true);

    const request$ = this.emailOAuthService
      .getThreadOthers(provider, emailId, mailbox ? mailbox : undefined)
      .pipe(
        tap(response => {
          const threadId = response.threadId || knownThreadId;
          if (!threadId) return;
          this._threadOthersLoaded.add(threadId);
          this.mergeThreadOthers(threadId, response.messages);
        }),
        catchError(() => of(empty)),
        finalize(() => {
          this._threadOthersRequests.delete(emailId);
          if (knownThreadId) this.markThreadOthersInFlight(knownThreadId, false);
        }),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this._threadOthersRequests.set(emailId, request$);
    return request$;
  }

  /** Fire-and-forget: the merged messages reach consumers through the store's signals. */
  private loadThreadOthers(emailId?: string | null): void {
    if (!emailId) return;
    this.fetchThreadOthers(emailId).pipe(takeUntil(this.destroy$)).subscribe();
  }

  private markThreadOthersInFlight(threadId: string, inFlight: boolean): void {
    const current = this._threadOthersInFlight();
    if (inFlight === current.includes(threadId)) return;
    this._threadOthersInFlight.set(
      inFlight ? [...current, threadId] : current.filter(id => id !== threadId)
    );
  }

  /**
   * True while the bodies for this thread are still on their way, so a message can
   * render a placeholder instead of an empty line. Always false on a deep link, where
   * the thread isn't in the list when the call goes out.
   */
  isThreadOthersLoading(threadId?: string | null): boolean {
    return !!threadId && this._threadOthersInFlight().includes(threadId);
  }

  /**
   * Merge by ID rather than replacing the message list: the thread copy owns
   * list-level state (Read/Favorite/Labels) and any already-downloaded attachment
   * data, the incoming copy owns the body. That also makes it irrelevant whether the
   * endpoint returns the whole thread or only the messages other than the opened one.
   */
  private mergeThreadOthers(threadId: string, messages: Email[]): void {
    if (!messages.length) return;

    const threads = this._threadsList();
    const idx = threads.findIndex(t => t.ThreadID === threadId);

    const existingById = new Map(
      idx === -1 ? [] : threads[idx].Messages.map(m => [m.ID, m] as const)
    );

    for (const incoming of messages) {
      if (!incoming?.ID) continue;
      const existing = existingById.get(incoming.ID);
      const merged = existing
        ? {
            ...this.mergeMessages(existing, incoming),
            Body: incoming.Body || existing.Body,
            HTMLBody: incoming.HTMLBody || existing.HTMLBody,
            Read: existing.Read,
            Favorite: existing.Favorite,
            Labels: existing.Labels ?? incoming.Labels,
          }
        : incoming;

      existingById.set(incoming.ID, merged);
      this.cacheMessageContent(merged);
    }

    const mergedMessages = Array.from(existingById.values());

    // On a deep link the thread isn't in the list yet. The cache above still applies,
    // and the list load will bring the thread in with its own copy.
    if (idx === -1) return;

    const newList = [...threads];
    newList[idx] = this.rebuildThread(threadId, mergedMessages);
    this._threadsList.set(newList);
    this._emailsList.set(newList.map(t => t.Latest));
  }

  /**
   * Load full content — including attachment base64 — for every message in `messages`
   * that carries attachments. Unlike `fetchMessageContent`, an incomplete cache entry
   * is never served: callers of this method need the bytes in hand when it emits
   * (e.g. to seed the New Application flow), not a later reactive update.
   *
   * Requests run with a small concurrency cap; a per-message failure is reported in
   * `failed` instead of collapsing the whole batch. `loaded` keeps the input order.
   */
  fetchMessagesAttachments(messages: Email[]): Observable<{ loaded: Email[]; failed: Email[] }> {
    const withAttachments = messages.filter(m => m.HasAttachments || !!m.Attachments?.length);
    if (withAttachments.length === 0) {
      return of({ loaded: [], failed: [] });
    }

    const order = new Map(withAttachments.map((m, i) => [m.ID, i]));
    const loaded: Email[] = [];
    const failed: Email[] = [];

    return from(withAttachments).pipe(
      mergeMap(message => {
        const cached = this._messageContentCache()[message.ID];
        if (cached && this.isContentComplete(cached)) {
          loaded.push(cached);
          return of(null);
        }

        return this.requestMessageContent(message.ID).pipe(
          tap(full => {
            const result = full ? (this._messageContentCache()[full.ID] ?? full) : null;
            // The response can carry attachment metadata without the base64 payload.
            // Treating that as loaded would put rows in the dialog that silently fail
            // to upload, so it is reported exactly like a failed request.
            if (!result || !this.isContentComplete(result)) {
              failed.push(message);
              return;
            }
            loaded.push(result);
          })
        );
      }, EmailAppStore.MESSAGE_CONTENT_CONCURRENCY),
      toArray(),
      map(() => ({
        loaded: loaded.sort((a, b) => (order.get(a.ID) ?? 0) - (order.get(b.ID) ?? 0)),
        failed,
      }))
    );
  }

  /** Max parallel message-content requests when loading a whole thread. */
  private static readonly MESSAGE_CONTENT_CONCURRENCY = 4;

  /**
   * Fetch a message's full content and fold it into the cache and its thread.
   * Concurrent callers for the same message share one round trip — expanding a whole
   * thread and starting the New Application flow otherwise fire a request each, per
   * message. Emits null when the request fails or no provider is connected.
   */
  private requestMessageContent(messageId: string): Observable<Email | null> {
    const provider = this.currentProvider$.value;
    if (!provider || !messageId) return of(null);

    const inFlight = this._contentRequests.get(messageId);
    if (inFlight) return inFlight;

    const mailbox = this._sharedMailbox();
    const request$ = this.emailOAuthService
      .getEmailContent(provider, messageId, true, mailbox ? mailbox : undefined)
      .pipe(
        tap(full => {
          if (full) {
            this.cacheMessageContent(full);
            // Update the message inside its thread so consumers see fresh body/attachments
            this.replaceMessageInThread(full);
          }
        }),
        map(full => full ?? null),
        catchError(() => of(null)),
        finalize(() => this._contentRequests.delete(messageId)),
        shareReplay({ bufferSize: 1, refCount: false })
      );

    this._contentRequests.set(messageId, request$);
    return request$;
  }

  /**
   * Write a message into the content cache, merging over whatever is already there
   * so a partial payload can never drop attachment data that was already downloaded.
   */
  private cacheMessageContent(email: Email): void {
    const cache = this._messageContentCache();
    const existing = cache[email.ID];
    this._messageContentCache.set({
      ...cache,
      [email.ID]: existing ? this.mergeMessages(existing, email) : email,
    });
  }

  /**
   * A message counts as fully loaded once every attachment carries its base64 data.
   * A message with no attachments at all is complete only if the list-shape flag
   * agrees there is nothing to lazy-load.
   */
  private isContentComplete(email: Email): boolean {
    const attachments = email.Attachments ?? [];
    if (attachments.length === 0) return !email.HasAttachments;
    return attachments.every(a => !!a.Data);
  }

  /**
   * Merge an incoming message payload over a base one, keeping the richer copy of
   * each attachment (the one that actually carries base64 data).
   */
  private mergeMessages(base: Email, incoming: Email): Email {
    return {
      ...base,
      ...incoming,
      Attachments: this.mergeAttachments(base.Attachments, incoming.Attachments),
    };
  }

  private mergeAttachments(base?: Attachment[], incoming?: Attachment[]): Attachment[] {
    if (!incoming?.length) return base ?? [];
    if (!base?.length) return incoming;

    const byFilename = new Map(base.map(a => [a.Filename, a]));
    return incoming.map(a => {
      const previous = byFilename.get(a.Filename);
      return !a.Data && previous?.Data ? { ...a, Data: previous.Data } : a;
    });
  }

  /**
   * Mark every message in a thread as read/unread (optimistic, fans out per-message API calls).
   */
  markThreadAsRead(threadId: string, read: boolean): Observable<unknown> {
    const thread = this.getThreadByEmailId(threadId);
    if (!thread) return of(null);

    const targets = thread.Messages.filter(m => m.Read !== read);
    if (targets.length === 0) return of(null);

    return from(targets).pipe(
      mergeMap(m => this.markEmailAsRead(m.ID, read, false)),
      finalize(() => this.recomputeThreadFromMessages(threadId))
    );
  }

  /**
   * Delete every message in a thread. Each message goes through deleteEmail (per-message API).
   */
  deleteThread(threadId: string): Observable<unknown> {
    const thread = this.getThreadByEmailId(threadId);
    if (!thread || thread.Messages.length === 0) return of(null);

    const messageIds = thread.Messages.map(m => m.ID);
    return from(messageIds).pipe(
      mergeMap(id => this.deleteEmail(id).pipe(catchError(() => of(null))))
    );
  }

  /**
   * Archive (or unarchive) every message in a thread.
   */
  archiveThread(threadId: string, archive: boolean): Observable<unknown> {
    const thread = this.getThreadByEmailId(threadId);
    if (!thread || thread.Messages.length === 0) return of(null);

    const provider = this.currentProvider$.value;
    if (!provider) return of(null);
    const mailbox = this._sharedMailbox();

    return from(thread.Messages).pipe(
      mergeMap(m =>
        this.emailOAuthService
          .updateEmailMessageFlags(m.ID, { archive }, mailbox ? mailbox : undefined)
          .pipe(catchError(() => of(null)))
      ),
      finalize(() => {
        // After archiving, the thread typically leaves the current label view — refresh
        this.reloadEmails();
      })
    );
  }

  /**
   * Mark every message in a thread as spam (or unspam).
   */
  markThreadAsSpam(threadId: string, spam: boolean): Observable<unknown> {
    const thread = this.getThreadByEmailId(threadId);
    if (!thread || thread.Messages.length === 0) return of(null);

    const mailbox = this._sharedMailbox();

    return from(thread.Messages).pipe(
      mergeMap(m =>
        this.emailOAuthService
          .updateEmailMessageFlags(m.ID, { spam }, mailbox ? mailbox : undefined)
          .pipe(catchError(() => of(null)))
      ),
      finalize(() => {
        this.reloadEmails();
      })
    );
  }

  /**
   * Mark every message in a thread as favorite (or unfavorite).
   */
  markThreadAsFavorite(threadId: string, favorite: boolean): Observable<unknown> {
    const thread = this.getThreadByEmailId(threadId);
    if (!thread || thread.Messages.length === 0) return of(null);

    const mailbox = this._sharedMailbox();

    return from(thread.Messages).pipe(
      mergeMap(m =>
        this.emailOAuthService
          .updateEmailMessageFlags(m.ID, { favorite }, mailbox ? mailbox : undefined)
          .pipe(catchError(() => of(null)))
      )
    );
  }

  /**
   * Replace a single message inside its thread (used after lazy-loading full content).
   */
  private replaceMessageInThread(updated: Email): void {
    const threads = this._threadsList();
    const threadId = updated.ThreadID;
    const idx = threads.findIndex(t => t.ThreadID === threadId);
    if (idx === -1) return;

    const thread = threads[idx];
    const newMessages = thread.Messages.map(m =>
      m.ID === updated.ID ? this.mergeMessages(m, updated) : m
    );

    const newList = [...threads];
    newList[idx] = this.rebuildThread(thread.ThreadID, newMessages);
    this._threadsList.set(newList);
    this._emailsList.set(newList.map(t => t.Latest));
  }

  /**
   * Recompute thread metadata (UnreadCount etc.) from its current messages.
   */
  private recomputeThreadFromMessages(threadId: string): void {
    const threads = this._threadsList();
    const idx = threads.findIndex(t => t.ThreadID === threadId);
    if (idx === -1) return;

    const rebuilt = this.rebuildThread(threadId, threads[idx].Messages);
    const newList = [...threads];
    newList[idx] = rebuilt;
    this._threadsList.set(newList);
    this._emailsList.set(newList.map(t => t.Latest));
  }

  private rebuildThread(threadId: string, messages: Email[]): EmailThread {
    const sorted = [...messages].sort(
      (a, b) => new Date(a.Date).getTime() - new Date(b.Date).getTime()
    );
    const latest = sorted[sorted.length - 1];
    return {
      ThreadID: threadId,
      Messages: sorted,
      Latest: latest,
      Subject: latest.Subject || sorted[0]?.Subject || '',
      Participants: Array.from(new Set(sorted.map(m => m.From).filter((v): v is string => !!v))),
      LatestDate: latest.Date,
      UnreadCount: sorted.filter(m => !m.Read).length,
      MessageCount: sorted.length,
      HasAttachments: sorted.some(m => (m.Attachments?.length || 0) > 0),
    };
  }

  /**
   * Load associations for specific thread IDs. Cache is keyed by threadId.
   */
  private loadAssociationsForThreads(threadIds: string[]): void {
    if (threadIds.length === 0) return;

    // Resolve threads from the loaded list so we can pull email addresses from their messages
    const threads = this._threadsList().filter(t => threadIds.includes(t.ThreadID));
    const emailAddresses = new Set<string>();
    threads.forEach(thread => {
      thread.Messages.forEach(m => {
        if (m.From) {
          const extracted = this.extractEmailAddress(m.From);
          if (extracted) emailAddresses.add(extracted);
        }
        if (m.To && Array.isArray(m.To)) {
          m.To.forEach(addr => {
            const extracted = this.extractEmailAddress(addr);
            if (extracted) emailAddresses.add(extracted);
          });
        }
      });
    });
    const uniqueEmailAddresses = Array.from(emailAddresses);

    const loadAssociations$ =
      uniqueEmailAddresses.length > 0
        ? // TODO: Implement this properly once API is ready
          this.emailAppService.getClientIdsByEmails([]).pipe(
            switchMap(clientIdsByEmails => {
              const currentClientIds = this._clientIdsByEmails();
              this._clientIdsByEmails.set({ ...currentClientIds, ...clientIdsByEmails });

              return this.emailAppService.getAssociations({ threadIds });
            }),
            catchError(() => {
              return this.emailAppService.getAssociations({ threadIds });
            })
          )
        : this.emailAppService.getAssociations({ threadIds });

    loadAssociations$.subscribe({
      next: associations => {
        const mergedAssociations: EmailAssociationsResponse = { ...associations };
        const clientIdsByEmails = this._clientIdsByEmails();

        threads.forEach(thread => {
          const threadId = thread.ThreadID;
          // Use the latest message's From as canonical lookup address for the thread
          const extractedEmailAddress = this.extractEmailAddress(thread.Latest.From || '');

          if (
            !mergedAssociations[threadId] &&
            extractedEmailAddress &&
            clientIdsByEmails[extractedEmailAddress]
          ) {
            const clientIds = clientIdsByEmails[extractedEmailAddress];
            if (clientIds && clientIds.length > 0) {
              mergedAssociations[threadId] = clientIds.map(
                clientId =>
                  ({
                    emailId: null,
                    threadId,
                    clientId,
                  }) as EmailLinkDto
              );
            }
          } else if (
            mergedAssociations[threadId] &&
            extractedEmailAddress &&
            clientIdsByEmails[extractedEmailAddress]
          ) {
            const existingAssociations = mergedAssociations[threadId];
            const clientIds = clientIdsByEmails[extractedEmailAddress];

            existingAssociations.forEach(assoc => {
              if (!assoc.clientId && clientIds && clientIds.length > 0) {
                assoc.clientId = clientIds[0];
              }
            });
          }
        });

        const currentAssociations = this._emailAssociations();
        this._emailAssociations.set({ ...currentAssociations, ...mergedAssociations });
      },
      error: () => {},
    });
  }

  /**
   * Load ingestion errors for the given thread IDs and replace what the store holds for
   * them. The backend returns errors keyed by threadId; we flatten them into a map keyed
   * by individual message ID (`emailId`) so consumers can look errors up per message.
   *
   * The response is authoritative for every thread we asked about: a message that no
   * longer appears has been re-processed successfully (OIQ-15383), so its error must go.
   * Plain merging would leave it on screen forever, because the response only lists what
   * is still failing. So we drop the requested threads' messages first, then re-add.
   *
   * `isBackground` marks the calls the user did not ask for — the 30s refresh tick and
   * pagination. It means "do not repeat yourself", not "stay quiet": a background failure
   * still snacks the first time it happens, then goes silent until the lookup recovers.
   * A dead endpoint is a state, not an event, so toasting it every 30 seconds would train
   * the user to dismiss the toast rather than read it. The initial load always snacks,
   * since there an absent warning icon reads as "all clear" when we never found out.
   */
  private loadErrorsForThreads(threadIds: string[], isBackground = false): void {
    if (threadIds.length === 0) return;

    this._isLoadingEmailErrors.set(true);

    this.emailAppService.getErrorAssociations(threadIds).subscribe({
      next: errorsByThread => {
        const nextErrors: EmailErrorAssociationsResponse = { ...this._emailErrors() };

        // 1. Clear every message of the threads we asked about. Scoped to those threads,
        //    so errors on threads outside this request are left alone.
        const requested = new Set(threadIds);
        this._threadsList()
          .filter(thread => requested.has(thread.ThreadID))
          .forEach(thread => {
            const messages = thread.Messages?.length ? thread.Messages : [thread.Latest];
            messages.forEach(message => {
              if (message) delete nextErrors[message.ID];
            });
          });

        // 2. Clear anything the response names directly. Covers a thread whose messages
        //    are not in the loaded list, and an explicit empty array for a resolved id.
        Object.entries(errorsByThread).forEach(([key, threadErrors]) => {
          delete nextErrors[key];
          threadErrors.forEach(err => delete nextErrors[err.emailId]);
        });

        // 3. Re-add what is still failing, keyed by individual message ID.
        Object.values(errorsByThread).forEach(threadErrors => {
          threadErrors.forEach(err => {
            (nextErrors[err.emailId] ??= []).push(err);
          });
        });

        this._emailErrors.set(nextErrors);
        this._emailErrorsError.set(null);
        this._isLoadingEmailErrors.set(false);
      },
      error: error => {
        // Leave `_emailErrors` untouched: the last known errors are better than silently
        // clearing every warning because one request failed.
        this._isLoadingEmailErrors.set(false);
        const message =
          error?.error?.message || error?.message || 'Failed to check emails for ingestion errors.';

        // Only the transition into a failing state is worth announcing. Staying broken is
        // not news, and `_emailErrorsError` keeps the condition readable for the UI.
        const wasHealthy = this._emailErrorsError() === null;
        this._emailErrorsError.set(message);

        if (!isBackground || wasHealthy) {
          this.notificationService.snack({
            content: message,
            variant: 'error',
            duration: 3000,
          });
        }
      },
    });
  }

  /**
   * Update a single association in the store, keyed by the id the link was made with.
   * If `linkDto` is null, the entry is removed (used by unlink flows).
   */
  private updateAssociationInStore(target: EmailLinkTarget, linkDto: EmailLinkDto | null): void {
    const key = emailLinkKey(target);
    if (!key) return;

    const currentAssociations = this._emailAssociations();
    if (linkDto === null) {
      const next = { ...currentAssociations };
      delete next[key];
      this._emailAssociations.set(next);
      return;
    }
    this._emailAssociations.set({
      ...currentAssociations,
      [key]: [linkDto],
    });
  }

  private checkOAuthStatus(): void {
    const oauthInProgress = localStorage.getItem('oauth_in_progress');
    const oauthProvider = localStorage.getItem('oauth_provider');

    if (oauthInProgress === 'true' && oauthProvider) {
      this._isOAuthInProgress.set(true);
      this._oauthProvider.set(oauthProvider as EmailProvider);
    }
  }

  createDraft(data: SendEmailData, provider?: EmailProvider, account?: string): Observable<any> {
    if (!provider || !account) {
      return throwError(() => new Error('No provider or account selected'));
    }
    const mailbox = this._sharedMailbox();

    return this.emailOAuthService
      .createDraft(provider, data, account, mailbox ? mailbox : undefined)
      .pipe(
        tap(() => {
          // this.reloadEmails();
        })
      );
  }

  updateDraft(
    draftId: string,
    data: SendEmailData,
    provider?: EmailProvider,
    account?: string
  ): Observable<any> {
    if (!provider || !account) {
      return throwError(() => new Error('No provider or account selected'));
    }

    const mailbox = this._sharedMailbox();

    return this.emailOAuthService
      .updateDraft(draftId, provider, data, account, mailbox ? mailbox : undefined)
      .pipe(
        tap(() => {
          this.reloadEmails();
        })
      );
  }

  /**
   * Clean up subscriptions and resources
   * This can be called manually if needed, or when the service is destroyed
   */
  destroy(): void {
    // Clean up periodic refresh subscription
    if (this.periodicRefreshSubscription) {
      this.periodicRefreshSubscription.unsubscribe();
      this.periodicRefreshSubscription = null;
    }

    // Complete destroy subject to clean up any remaining subscriptions
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Reset all store state to initial values
   */
  resetStore(): void {
    // Reset BehaviorSubjects
    this.currentProvider$.next(null);
    this.emailFilters$.next({
      labels: null,
      pageToken: null,
      query: null,
    });
    this._selectedEmailId$.next(null);

    // Reset signals
    this._isOAuthInProgress.set(false);
    this._oauthProvider.set(null);
    this._nextPageToken.set(null);
    this._hasMore.set(false);
    this._emailsList.set([]);
    this._threadsList.set([]);
    this._messageContentCache.set({});
    this._attachmentsInFlight.clear();
    this._contentRequests.clear();
    this._threadOthersLoaded.clear();
    this._threadOthersRequests.clear();
    this._threadOthersInFlight.set([]);
    this._emailAssociations.set({});
    this._messageAssociations.set({});
    this._messageAssociationsInFlight.clear();
    this._emailErrors.set({});
    this._emailErrorsError.set(null);
    this._isLoadingEmailErrors.set(false);
    this._clientIdsByEmails.set({});
    this._isRefreshing.set(false);
    this._excludedProvider = null;
    this._initialized = false;

    // Clean up periodic refresh
    if (this.periodicRefreshSubscription) {
      this.periodicRefreshSubscription.unsubscribe();
      this.periodicRefreshSubscription = null;
    }

    // Clear shared mailbox
    this.setSharedMailbox('');
  }
}

export const EMAIL_APP_STORE = new InjectionToken<EmailAppStore>('EMAIL_APP_STORE');

export const EMAIL_APP_STORE_PROVIDER = {
  provide: EMAIL_APP_STORE,
  useFactory: () => new EmailAppStore(),
};
